import {Slider, Stack} from "@mui/material";
import {useSigma} from "@react-sigma/core";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Form} from "react-bootstrap";
import {useSubNetworkContext} from "../../contexts/SubNetworkContext";
import {useTheCellMapContext} from "../../contexts/TheCellMapContext";
import {useSliderStyle} from "../../styles/SliderStyle";
import {floorToDecimals} from "../../utils/number";
import HideLabelToggleButton from "../HideLabelToggleButton";
import Legend from "../Legend";
import LegendInfoButton from "../LegendInfoButton";
import {UIBottomLeft, UIBottomRight, UITopLeft, UITopRight} from "../layout/UI";
import ListLinkButton from "../ListLinkButton";
import NetworkLinkButton from "../NetworkLinkButton";
import NodeContextMenu from "../NodeContextMenu";
import OverlayLinkButton from "../OverlayLinkButton";
import ResetGraphButton from "../ResetGraphButton";
import SearchBox from "../SearchBox";

import "bootstrap-icons/font/bootstrap-icons.css";
import "../../css/ui.css";
import Screenshot from "../SigmaScreenshot";
import {TheCellMapZoomControl} from "../TheCellMapZoomControl";
import NoLibraryMessagePanel from "./NoLibraryMessagePanel";
import SubNetworkLegend from "./SubNetworkLegend";

const SLIDER = {
  min: 0.2,
  global: 0.41,
  max: 1,
  step: 0.01,
};

const SubNetworkUserInterface = () => {
  const sigma = useSigma();
  const graph = sigma.getGraph();

  const {selectedGenes} = useTheCellMapContext();
  const {maxLoadedPcc, displayedTerms, subNetworkCutoff, setSubNetworkCutoff} = useSubNetworkContext();
  const roundedMaxLoadedPcc = useMemo(() => floorToDecimals(maxLoadedPcc ?? SLIDER.max), [maxLoadedPcc]);

  /* Local state for the cutoff slider, to keep the label updated until handle is let go and new value committed. */
  const [localCutoff, setLocalCutoff] = useState<number>(() => floorToDecimals(subNetworkCutoff));
  const [dragging, setDragging] = useState(false);
  const [editingCutoff, setEditingCutoff] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const sliderStyle = useSliderStyle(roundedMaxLoadedPcc, dragging);

  const marks = useMemo(() => [{
      value: SLIDER.min,
      label: SLIDER.min.toFixed(1),
    }, {
      value: SLIDER.global,
      label: 'Global network PCC',
    }, {
      value: roundedMaxLoadedPcc,
      label: 'Selection max',
    }, {
      value: SLIDER.max,
      label: SLIDER.max.toFixed(0),
    }], [roundedMaxLoadedPcc]
  );

  /**
   * Update the local cutoff when the global cutoff changes.
   */
  useEffect(() => {
    setLocalCutoff(floorToDecimals(subNetworkCutoff));
  }, [subNetworkCutoff]);

  useEffect(() => {
    if (editingCutoff && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCutoff, localCutoff]);

  /**
   * Restrict the slider value to the maximum loaded PCC, if defined.
   */
  const restrictSlider = (newValue: number | number[]) => {
    const value = newValue as number;
    const normalizedMax = Number.isFinite(roundedMaxLoadedPcc) ? Math.min(roundedMaxLoadedPcc, SLIDER.max) : SLIDER.max;
    const clampedValue = Math.max(SLIDER.min, Math.min(value, normalizedMax));
    const restrictedValue = floorToDecimals(clampedValue);
    return {restrictedValue, isRestricted: restrictedValue !== value};
  }

  /**
   * Update the local cutoff/label when we move the handle.
   */
  const handleSliderChange = (_e: any, val: number | number[]) => {
    setLocalCutoff(val as number);
  };

  /**
   * Set the actual cutoff when we let go of the handle.
   */
  const commitCutoffValue = (value: number | number[]) => {
    const {restrictedValue} = restrictSlider(value);
    setLocalCutoff(restrictedValue);
    setSubNetworkCutoff(restrictedValue);
  };

  /**
   * Commit the value when the slider handle is released.
   */
  const handleSliderCommitted = (_e: any, val: number | number[]) => {
    commitCutoffValue(val);
    setDragging(false);
  };

  /**
   * Inform user we don't have data if PCC is over roundedMaxLoadedPcc.
   */
  const labelFormat = useCallback((v: number) =>
      localCutoff > roundedMaxLoadedPcc ? "No data at this cutoff" : v.toFixed(2)
    , [roundedMaxLoadedPcc, localCutoff]);

  /**
   * Show the label only if the cutoff is a little bit over the max PCC. It avoids overlapping the marks.
   */
  const labelDisplay = useMemo(() =>
      localCutoff > roundedMaxLoadedPcc * 1.1 ? "auto" : "off"
    , [roundedMaxLoadedPcc, localCutoff]);

  const beginManualInput = () => {
    if (!Number.isFinite(roundedMaxLoadedPcc)) return;
    setEditingCutoff(true);
  };

  const cancelManualInput = () => {
    if (inputRef.current) {
      inputRef.current.value = cutoffText;
    }
    setEditingCutoff(false);
  };

  const commitManualInput = () => {
    const nextValue = parseFloat(inputRef.current?.value ?? '');
    if (Number.isNaN(nextValue)) {
      cancelManualInput();
      return;
    }
    commitCutoffValue(nextValue);
    setEditingCutoff(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur(); // Blur commits
    }
    if (e.key === "Escape") {
      e.preventDefault();
      cancelManualInput();
    }
  };

  const sliderDisabled = !Number.isFinite(roundedMaxLoadedPcc);
  const cutoffText = localCutoff.toFixed(2);

  return <>
    {!selectedGenes.length &&
        <div className="position-absolute h-100 w-100 top-0 d-flex flex-fill justify-content-center align-items-center">
            <span className="text-white">Search genes to view subnetwork.</span>
        </div>
    }

    <UITopLeft>
      <ResetGraphButton/>
      <SearchBox/>
      <ListLinkButton/>
      <NetworkLinkButton/>
    </UITopLeft>

    <UITopRight direction="column" align="end">
      <OverlayLinkButton/>
      {(graph.size) &&
          <Stack id="sub-slider" spacing={2} direction="column" className="d-flex gap-2 align-items-center mt-3">
              <div className="d-flex align-items-center h-100">
                  <div className="d-flex align-items-center h-100">
                      <Slider
                          aria-label="Cutoff"
                          orientation="vertical"
                          getAriaValueText={labelFormat}
                          valueLabelFormat={labelFormat}
                          marks={marks}
                          min={SLIDER.min}
                          max={SLIDER.max}
                          step={SLIDER.step}
                          value={localCutoff}
                          valueLabelDisplay={labelDisplay}
                          disabled={sliderDisabled}
                          onChange={handleSliderChange}
                          onChangeCommitted={handleSliderCommitted}
                          onMouseDown={() => setDragging(true)}
                          onTouchStart={() => setDragging(true)}
                          onTouchEnd={() => setDragging(false)}
                          sx={sliderStyle}
                      />
                      <span
                          aria-hidden
                          className="text-white small ms-2 user-select-none"
                          style={{writingMode: 'vertical-rl'}}>
                          PCC threshold
                      </span>
                  </div>
              </div>
            {editingCutoff ? (
              <Form.Control
                ref={inputRef}
                type="text"
                inputMode="decimal"
                size="sm"
                className="text-center font-monospace subnetwork-cutoff-control"
                defaultValue={cutoffText}
                onBlur={commitManualInput}
                onKeyDown={handleInputKeyDown}
                spellCheck={false}
                autoComplete="off"
              />
            ) : (
              <button
                type="button"
                className="btn btn-outline-light btn-sm font-monospace subnetwork-cutoff-control"
                onClick={beginManualInput}
                disabled={sliderDisabled}
                aria-label="Set cutoff value manually"
              >
                {cutoffText}
              </button>
            )}
          </Stack>}
    </UITopRight>

    <UIBottomLeft direction="row" align="end" allowScreenshot>
      <div className="d-flex flex-column-reverse gap-2" data-html2canvas-ignore>
        <TheCellMapZoomControl/>
        <HideLabelToggleButton/>
        <LegendInfoButton showLegend={showLegend} onToggle={() => setShowLegend(v => !v)}/>
        <Screenshot fileName="thecellmap_human_subnetwork" sigma={sigma} legendAnnotations={displayedTerms} showLegend={showLegend}/>
      </div>
      {showLegend && (
        <Legend>
          {selectedGenes.length > 0 && displayedTerms.length > 0 && <SubNetworkLegend/>}
        </Legend>
      )}
    </UIBottomLeft>

    <UIBottomRight containerClassName="message-panel-container">
      <NoLibraryMessagePanel/>
    </UIBottomRight>

    <NodeContextMenu/>
  </>
};

export default SubNetworkUserInterface;
